import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Image from '../../components/AppImage';

const AboutEcosystem = () => {
  const [activeSection, setActiveSection] = useState('mission');

  const teamMembers = [
    {
      id: 1,
      name: "Alex Chen",
      role: "Founder & CEO",
      experience: 12,
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face",
      bio: "Visionary leader with 12+ years in digital transformation. Former CTO at Fortune 500 companies, passionate about bridging technology and business strategy.",
      skills: ["Strategic Leadership", "Digital Transformation", "Team Building", "Innovation"],
      linkedin: "https://linkedin.com/in/alexchen",
      github: "https://github.com/alexchen"
    },
    {
      id: 2,
      name: "Sarah Rodriguez",
      role: "CTO & Technical Architect",
      experience: 10,
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200&h=200&fit=crop&crop=face",
      bio: "Technical architect with deep expertise in scalable systems. Led development teams at Google and Amazon, specializing in cloud-native solutions.",
      skills: ["System Architecture", "Cloud Computing", "DevOps", "Team Leadership"],
      linkedin: "https://linkedin.com/in/sarahrodriguez",
      github: "https://github.com/sarahrodriguez"
    },
    {
      id: 3,
      name: "Michael Kim",
      role: "Head of Design & UX",
      experience: 8,
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face",
      bio: "Design leader focused on creating exceptional user experiences. Former design director at Apple, expert in design systems and user research.",
      skills: ["UX Design", "Design Systems", "User Research", "Product Strategy"],
      linkedin: "https://linkedin.com/in/michaelkim",
      github: "https://github.com/michaelkim"
    },
    {
      id: 4,
      name: "Emily Watson",
      role: "VP of Engineering",
      experience: 9,
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face",
      bio: "Engineering leader with expertise in building high-performing teams. Former engineering manager at Microsoft, passionate about code quality and team growth.",
      skills: ["Engineering Management", "Code Quality", "Team Development", "Agile"],
      linkedin: "https://linkedin.com/in/emilywatson",
      github: "https://github.com/emilywatson"
    },
    {
      id: 5,
      name: "David Thompson",
      role: "Head of Business Development",
      experience: 11,
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=face",
      bio: "Business development expert with deep industry connections. Former VP at Salesforce, specializing in enterprise partnerships and growth strategies.",
      skills: ["Business Development", "Partnerships", "Growth Strategy", "Enterprise Sales"],
      linkedin: "https://linkedin.com/in/davidthompson",
      github: "https://github.com/davidthompson"
    }
  ];

  const values = [
    {
      icon: "Zap",
      title: "Innovation First",
      description: "We push boundaries and embrace cutting-edge technologies to deliver solutions that set new industry standards."
    },
    {
      icon: "Users",
      title: "People-Centric",
      description: "Our team and clients are at the heart of everything we do. We build relationships that last and grow together."
    },
    {
      icon: "Shield",
      title: "Quality & Security",
      description: "We never compromise on quality or security. Every line of code is crafted with precision and care."
    },
    {
      icon: "Target",
      title: "Results-Driven",
      description: "We measure success by the tangible impact we create for our clients and their business objectives."
    }
  ];

  const stats = [
    { number: "150+", label: "Projects Delivered", icon: "CheckCircle" },
    { number: "50+", label: "Happy Clients", icon: "Heart" },
    { number: "5+", label: "Years Experience", icon: "Award" },
    { number: "99.9%", label: "Client Satisfaction", icon: "Star" }
  ];

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand/10 via-secondary/5 to-accent/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gradient-brand mb-6">
              About Our Ecosystem
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
              We're a passionate team of innovators, creators, and problem-solvers dedicated to 
              transforming businesses through exceptional technology solutions.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-full border border-border">
                <Icon name="Users" size={16} />
                <span className="text-sm font-medium">Expert Team</span>
              </div>
              <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-full border border-border">
                <Icon name="Zap" size={16} />
                <span className="text-sm font-medium">Innovation</span>
              </div>
              <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-full border border-border">
                <Icon name="Target" size={16} />
                <span className="text-sm font-medium">Results-Driven</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat.icon} size={24} className="text-secondary" />
                </div>
                <div className="text-3xl font-bold text-text-primary mb-2">{stat.number}</div>
                <div className="text-sm text-text-secondary">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {[
              { id: 'mission', label: 'Our Mission', icon: 'Target' },
              { id: 'team', label: 'Our Team', icon: 'Users' },
              { id: 'values', label: 'Our Values', icon: 'Heart' },
              { id: 'culture', label: 'Our Culture', icon: 'Zap' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all flex items-center space-x-2 ${
                  activeSection === tab.id
                    ? 'bg-brand text-white shadow-lg'
                    : 'bg-card text-text-secondary hover:bg-muted'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Mission Section */}
          {activeSection === 'mission' && (
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-text-secondary mb-8 leading-relaxed">
                To empower businesses with innovative technology solutions that drive growth, 
                enhance efficiency, and create lasting competitive advantages in the digital age.
              </p>
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="text-left">
                  <h3 className="text-xl font-semibold text-text-primary mb-4">What We Do</h3>
                  <ul className="space-y-3 text-text-secondary">
                    <li className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Custom software development tailored to your business needs</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Digital transformation consulting and strategy</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Cloud-native solutions and infrastructure</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>AI and machine learning integration</span>
                    </li>
                  </ul>
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-semibold text-text-primary mb-4">Our Approach</h3>
                  <ul className="space-y-3 text-text-secondary">
                    <li className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Collaborative partnership with transparent communication</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Agile methodology with rapid iteration and feedback</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Focus on scalability, security, and performance</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Continuous support and optimization</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Team Section */}
          {activeSection === 'team' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                  Meet Our Leadership Team
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Experienced professionals passionate about technology and committed to delivering exceptional results.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {teamMembers.map((member) => (
                  <div key={member.id} className="bg-card rounded-brand p-6 hover-lift transition-all">
                    <div className="flex items-center space-x-4 mb-4">
                      <Image
                        src={member.avatar}
                        alt={member.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary">{member.name}</h3>
                        <p className="text-sm text-secondary font-medium">{member.role}</p>
                        <p className="text-xs text-text-secondary">{member.experience}+ years experience</p>
                      </div>
                    </div>
                    <p className="text-sm text-text-secondary mb-4">{member.bio}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {member.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-muted text-xs text-text-secondary rounded-full"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                    <div className="flex space-x-3">
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-text-secondary hover:text-secondary transition-colors"
                      >
                        <Icon name="Linkedin" size={16} />
                      </a>
                      <a
                        href={member.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-text-secondary hover:text-secondary transition-colors"
                      >
                        <Icon name="Github" size={16} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Values Section */}
          {activeSection === 'values' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                  Our Core Values
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  The principles that guide everything we do and shape our culture.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {values.map((value, index) => (
                  <div key={index} className="bg-card rounded-brand p-6 hover-lift transition-all">
                    <div className="w-12 h-12 bg-secondary/10 rounded-brand flex items-center justify-center mb-4">
                      <Icon name={value.icon} size={24} className="text-secondary" />
                    </div>
                    <h3 className="text-xl font-semibold text-text-primary mb-3">{value.title}</h3>
                    <p className="text-text-secondary">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Culture Section */}
          {activeSection === 'culture' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
                  Our Culture
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  A collaborative environment where innovation thrives and every voice matters.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-card rounded-brand p-6">
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Work Environment</h3>
                    <ul className="space-y-2 text-text-secondary">
                      <li>• Flexible remote-first culture</li>
                      <li>• Collaborative team environment</li>
                      <li>• Continuous learning and development</li>
                      <li>• Work-life balance focus</li>
                    </ul>
                  </div>
                  <div className="bg-card rounded-brand p-6">
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Growth & Development</h3>
                    <ul className="space-y-2 text-text-secondary">
                      <li>• Regular training and workshops</li>
                      <li>• Conference attendance support</li>
                      <li>• Mentorship programs</li>
                      <li>• Career advancement opportunities</li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-card rounded-brand p-6">
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Innovation & Creativity</h3>
                    <ul className="space-y-2 text-text-secondary">
                      <li>• Hackathon events</li>
                      <li>• Innovation time allocation</li>
                      <li>• Cross-functional projects</li>
                      <li>• Technology experimentation</li>
                    </ul>
                  </div>
                  <div className="bg-card rounded-brand p-6">
                    <h3 className="text-xl font-semibold text-text-primary mb-3">Community & Impact</h3>
                    <ul className="space-y-2 text-text-secondary">
                      <li>• Open source contributions</li>
                      <li>• Community outreach programs</li>
                      <li>• Environmental sustainability</li>
                      <li>• Diversity and inclusion initiatives</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-brand-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Work with Our Team?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's discuss how our expertise and proven methodologies can transform your next project.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              variant="outline"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              Schedule a Consultation
            </Button>
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="bg-white text-secondary hover:bg-white/90"
            >
              Start Your Project
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutEcosystem;